import React from 'react';
import SiteFooter from '../components/sections/SiteFooter';
import SubmitTipButton from '../components/SubmitTipButton';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import PageSeoTag from '../components/PageSeoTag';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl md:text-2xl font-black text-white mb-3 border-l-4 border-red-500 pl-3">
      {title}
    </h2>
    <div className="text-gray-300 text-sm md:text-base leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const Privacy = () => {
  return (
    <div className="min-h-screen bg-black text-white" data-testid="privacy-page">
      <PageSeoTag pageKey="privacy" />
      <header className="bg-zinc-900/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-300 hover:text-white text-sm font-bold" data-testid="privacy-back-home">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <span className="text-sm font-black tracking-widest text-red-400">PRIVACY POLICY</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 mb-4">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-[11px] font-black tracking-[0.2em] text-emerald-400 uppercase">
              Your Privacy, Protected
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black mb-2">Privacy Policy</h1>
          <p className="text-xs text-gray-500">Last updated: 1 June 2026</p>
        </div>

        <Section title="Who We Are">
          <p>
            Outlaw Media is an independent crime news outlet headquartered in Dubai, United Arab
            Emirates, reporting from Melbourne, Australia. This policy explains what data we
            collect when you visit outlawmedia.com.au, how we use it, and the rights you have
            over it.
          </p>
        </Section>

        <Section title="What We Collect">
          <p><strong className="text-white">Newsletter signups:</strong> If you subscribe to our newsletter, we collect your email address only.</p>
          <p><strong className="text-white">Page analytics:</strong> Anonymous page-view counts and approximate location (country/city) used to understand our audience. No personally identifying information is linked to these records.</p>
          <p><strong className="text-white">18+ video engagement:</strong> Likes, comments, and view counts are stored against a randomly generated browser-side identifier - not your name, email, or IP.</p>
        </Section>

        <Section title="What We DO NOT Collect">
          <p>
            <strong className="text-red-400">Anonymous tips:</strong> When you submit a tip via the "Submit a Tip" button,
            we do not store your IP address, device fingerprint, browser identifier, or any other identifying
            metadata. Tips are forwarded directly to our newsroom and discarded by the system. Your identity
            is unknown to us unless you choose to provide it in the tip itself.
          </p>
          <p>
            We do not sell, rent, or share any of your data with third-party advertisers.
          </p>
        </Section>

        <Section title="Age Verification">
          <p>
            Our 18+ section requires age verification. We store only a "verified" flag in your browser's
            session storage - no date of birth, photo, or document is collected. The flag is cleared when
            you close your browser.
          </p>
        </Section>

        <Section title="Third-Party Services We Use">
          <p>
            <strong className="text-white">Cloudflare R2:</strong> Stores video files only. No user data.<br />
            <strong className="text-white">Resend:</strong> Delivers tip emails to our newsroom. Anonymous; only the tip content is transmitted.<br />
            <strong className="text-white">MongoDB:</strong> Stores newsletter emails, video records, and anonymous engagement counters.
          </p>
        </Section>

        <Section title="Your Rights">
          <p>
            Under Australian Privacy Principles you can request access to, correction of, or deletion of
            any data we hold about you. Email <a href="mailto:ryan@outlawmedia.com.au" className="text-red-400 underline">ryan@outlawmedia.com.au</a> with your request.
          </p>
          <p>
            To unsubscribe from the newsletter, click the unsubscribe link in any email or email us.
          </p>
        </Section>

        <Section title="Changes to This Policy">
          <p>
            We may update this policy from time to time. Material changes will be flagged on this page
            with an updated "Last updated" date.
          </p>
        </Section>
      </main>

      <SiteFooter />
      <SubmitTipButton />
    </div>
  );
};

export default Privacy;
